/**
 * Pulling co-located map pins apart, and saying how much each one is worth.
 *
 * Why (2026-08-31)
 * ----------------
 * The CRM now places a customer from two sources: the geocoder (street
 * accurate) and an offline gazetteer (the city, the state, or the country).
 * The fallback is what got Saudi Arabia's air bases back onto the map — but it
 * also means many customers share ONE coordinate. Ten bases resolved to
 * "Saudi Arabia" stack into a single marker: the map shows one pin and the
 * operator counts nine missing customers.
 *
 * So we fan a shared coordinate out into a ring. Two rules make that honest:
 *
 *   1. It is DETERMINISTIC. The same customer lands in the same spot on every
 *      render, every session, every browser. A pin that jitters between
 *      renders is worse than a stacked one.
 *   2. It is SMALL. The offsets are tens of kilometres — invisible at world
 *      zoom, a few pixels apart at country zoom, clearly separate when you
 *      zoom to the cluster. Never large enough to cross a border.
 *
 * The displacement is presentation only. `latitude`/`longitude` in the
 * database stay exactly what the geocoder said; nothing here writes back.
 */

/** The minimum a pin must carry to be placed. */
export interface Pinnable {
  id: number | string
  latitude?: number | null
  longitude?: number | null
  /** "exact" | "city" | "subdivision" | "country" | null (row predates the column). */
  coordinate_precision?: string | null
}

export type SpreadPin<T extends Pinnable> = T & {
  /** Where to DRAW this pin. Equals the stored coordinate when nothing collides. */
  latitude: number
  longitude: number
  /** The stored coordinate, untouched — for anything that must not lie. */
  trueLatitude: number
  trueLongitude: number
  /** How many customers (including this one) share the stored coordinate. */
  sharedWith: number
  /** True when this pin was nudged off its stored coordinate to be visible. */
  displaced: boolean
}

/**
 * Coordinates rounded to this many decimals count as "the same place".
 * Three decimals is ~110 m — well under one pixel at any zoom where a human is
 * looking at more than one customer.
 */
const COLLISION_DECIMALS = 3

/**
 * Radius of the first ring, in SCREEN PIXELS.
 *
 * Pixels, not degrees, because "far enough apart to see" is a fact about the
 * screen, not about the Earth. A fixed 10 km ring is invisible at world zoom
 * (well under one pixel) and enormous at street zoom; a fixed 14 px ring reads
 * the same at every zoom the operator uses. Markers are 20-25 px wide, so 14 px
 * of separation leaves each one clearly distinguishable without scattering
 * them across the country.
 */
const RING_PIXELS = 14

/**
 * ...but never more than this in degrees, whatever the zoom says.
 *
 * At world zoom one pixel is ~35 km, so an uncapped pixel ring would fling a
 * cluster 500 km apart — across a border, into a different customer's country.
 * Capping at 0.75 deg (~83 km) means a cluster stays a cluster when zoomed out
 * (a truthful blob) and separates cleanly as soon as anyone zooms in to count
 * it. Being slightly hard to count at world zoom is a much smaller lie than
 * putting a Saudi customer in Iraq.
 */
const MAX_RING_DEGREES = 0.75

/** Web Mercator ground resolution at zoom 0, in metres per pixel. */
const METRES_PER_PIXEL_AT_ZOOM_0 = 156543.03392

const METRES_PER_DEGREE_LATITUDE = 111320

/** The default Leaflet zoom the world map opens at. */
const DEFAULT_ZOOM = 2

/**
 * How many degrees of latitude `RING_PIXELS` is worth here and now. Latitude
 * degrees shrink on screen towards the poles under Mercator, which is why the
 * latitude matters and not just the zoom.
 */
function ringDegrees(latitude: number, zoom: number): number {
  const metresPerPixel =
    (METRES_PER_PIXEL_AT_ZOOM_0 * Math.cos((latitude * Math.PI) / 180)) / Math.pow(2, zoom)
  return (RING_PIXELS * metresPerPixel) / METRES_PER_DEGREE_LATITUDE
}

/** The golden angle. Successive points never line up, at any count. */
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5))

const MAX_LATITUDE = 85 // Web Mercator gives up past here.

function collisionKey(latitude: number, longitude: number): string {
  return `${latitude.toFixed(COLLISION_DECIMALS)},${longitude.toFixed(COLLISION_DECIMALS)}`
}

function isPlaceable(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value)
}

function wrapLongitude(longitude: number): number {
  // Keep a nudge near the antimeridian on the map rather than off the edge.
  return ((((longitude + 180) % 360) + 360) % 360) - 180
}

/**
 * Fan every group of customers sharing one coordinate out into a spiral, so a
 * stack of ten reads as ten. Customers with no coordinate are dropped — they
 * cannot be drawn — and singletons come back exactly where they were.
 *
 * Order in, order out: the sort is by id, so the spiral is stable across
 * renders even when the incoming array is reordered by a filter. `zoom` only
 * scales the ring — it never changes which pin goes where in it, so a marker
 * keeps its bearing from the cluster centre as the operator zooms.
 */
export function spreadOverlappingPins<T extends Pinnable>(
  customers: T[],
  zoom: number = DEFAULT_ZOOM,
): SpreadPin<T>[] {
  const groups = new Map<string, T[]>()

  for (const customer of customers) {
    const latitude = Number(customer.latitude)
    const longitude = Number(customer.longitude)
    if (customer.latitude == null || customer.longitude == null) continue
    if (!isPlaceable(latitude) || !isPlaceable(longitude)) continue
    if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) continue

    const key = collisionKey(latitude, longitude)
    const group = groups.get(key)
    if (group) group.push(customer)
    else groups.set(key, [customer])
  }

  const placed: SpreadPin<T>[] = []

  for (const group of groups.values()) {
    const ordered = [...group].sort((a, b) => String(a.id).localeCompare(String(b.id), undefined, { numeric: true }))

    ordered.forEach((customer, index) => {
      const trueLatitude = Number(customer.latitude)
      const trueLongitude = Number(customer.longitude)

      // The first of a group keeps the real spot; the rest spiral around it.
      // A lone customer therefore never moves at all.
      let latitude = trueLatitude
      let longitude = trueLongitude

      if (index > 0) {
        const radius = Math.min(ringDegrees(trueLatitude, zoom) * Math.sqrt(index), MAX_RING_DEGREES)
        const angle = index * GOLDEN_ANGLE

        // Longitude degrees shrink towards the poles. Dividing by cos(lat)
        // keeps the ring circular on screen instead of a squashed ellipse.
        const shrink = Math.max(Math.cos((trueLatitude * Math.PI) / 180), 0.15)

        latitude = trueLatitude + radius * Math.cos(angle)
        longitude = trueLongitude + (radius * Math.sin(angle)) / shrink

        latitude = Math.max(-MAX_LATITUDE, Math.min(MAX_LATITUDE, latitude))
        longitude = wrapLongitude(longitude)
      }

      placed.push({
        ...customer,
        latitude,
        longitude,
        trueLatitude,
        trueLongitude,
        sharedWith: ordered.length,
        displaced: index > 0,
      })
    })
  }

  return placed
}

/**
 * How to describe a pin's accuracy to an operator, or null when there is
 * nothing worth saying (a rooftop pin, or a row written before the CRM
 * recorded precision at all — in which case claiming anything would be a
 * guess).
 *
 * "street" joined the scale on 2026-09-02, when the geocoder started reading
 * Nominatim's `addresstype` instead of stamping every believed hit "exact". A
 * road centroid is a real location and worth far more than a state, but it is
 * not the front door, and the difference is one an operator can act on: it
 * usually means the address on file has a house number the map could not find.
 */
export function precisionNote(precision?: string | null): string | null {
  switch (precision) {
    case "street":
      return "On this street — the exact building could not be resolved"
    case "city":
      return "Approximate — placed at the centre of the town"
    case "subdivision":
      return "Approximate — placed at the centre of the state or region"
    case "country":
      return "Approximate — only the country is known"
    default:
      return null
  }
}
